import type { Frequency, IsoDate, RecurringTemplate, Transaction } from './model';

/**
 * Recurring bills are tracked per period: a monthly bill is either recorded
 * for this month or still due. Periods are calendar based (month, quarter,
 * year) so "this month" means the same thing on every screen.
 */
export interface Period {
  start: IsoDate;
  end: IsoDate;
}

const pad = (n: number) => String(n).padStart(2, '0');
const localDate = (ms: number) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const daysIn = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

export function periodOf(frequency: Frequency, date: IsoDate): Period {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const first = frequency === 'monthly' ? month : frequency === 'quarterly' ? month - ((month - 1) % 3) : 1;
  const last = frequency === 'monthly' ? month : frequency === 'quarterly' ? first + 2 : 12;
  return { start: `${year}-${pad(first)}-01`, end: `${year}-${pad(last)}-${pad(daysIn(year, last))}` };
}

/** The period right before the one containing `date`. */
export function previousPeriod(frequency: Frequency, date: IsoDate): Period {
  const start = periodOf(frequency, date).start;
  const d = new Date(`${start}T00:00:00Z`);
  d.setUTCDate(0);
  return periodOf(frequency, d.toISOString().slice(0, 10));
}

/** Due date inside a period, the day clamped to the month (31 → 30 Sep, 28/29 Feb). */
export function dueDateIn(template: Pick<RecurringTemplate, 'dayOfMonth'>, period: Period): IsoDate | null {
  if (template.dayOfMonth === null) return null;
  const year = Number(period.start.slice(0, 4));
  const month = Number(period.start.slice(5, 7));
  return `${year}-${pad(month)}-${pad(Math.min(template.dayOfMonth, daysIn(year, month)))}`;
}

export type BillStatus = 'due' | 'overdue' | 'recorded';

export interface BillRow {
  template: RecurringTemplate;
  period: Period;
  dueDate: IsoDate | null;
  status: BillStatus;
  /** Entries recorded against this bill in the period. */
  recorded: Transaction[];
  /** The latest entry before this period: "last month" for a monthly bill. */
  previous: Transaction | null;
  /** What one tap records: the fixed amount, or a variable bill's last amount (null with no history). */
  suggestedMinor: number | null;
}

/**
 * The bills of a budget for the period containing `today`. `linked` are
 * live entries that carry a recurring template id (any date); the caller
 * passes enough history for "last month" to be found.
 */
export function billRows(templates: RecurringTemplate[], linked: Transaction[], today: IsoDate): BillRow[] {
  const byTemplate = new Map<string, Transaction[]>();
  for (const tx of linked) {
    if (!tx.recurringTemplateId) continue;
    const list = byTemplate.get(tx.recurringTemplateId) ?? [];
    list.push(tx);
    byTemplate.set(tx.recurringTemplateId, list);
  }
  const rows = templates
    .filter((t) => t.active)
    .map((template): BillRow => {
      const period = periodOf(template.frequency, today);
      const entries = (byTemplate.get(template.id) ?? []).slice().sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
      const recorded = entries.filter((e) => e.date >= period.start && e.date <= period.end);
      const previous = entries.find((e) => e.date < period.start) ?? null;
      const dueDate = dueDateIn(template, period);
      // A bill added after this period's due day is not "overdue": it did not exist yet.
      const addedBefore = !template.createdAt || !dueDate || localDate(template.createdAt) <= dueDate;
      const status: BillStatus = recorded.length > 0 ? 'recorded' : dueDate && dueDate < today && addedBefore ? 'overdue' : 'due';
      // A fixed bill's amount lives on the template (recording a new amount moves it, so a rent rise sticks).
      const suggestedMinor = template.kind === 'fixed' ? template.amountMinor : (previous?.amountMinor ?? null);
      return { template, period, dueDate, status, recorded, previous, suggestedMinor };
    });
  const rank = { overdue: 0, due: 1, recorded: 2 } as const;
  return rows.sort(
    (a, b) =>
      rank[a.status] - rank[b.status] ||
      (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999') ||
      a.template.name.localeCompare(b.template.name),
  );
}

/** Whether the previous entry sits in the period right before (then it is "last month", not just "last time"). */
export function isLastPeriod(row: Pick<BillRow, 'previous' | 'template'>, today: IsoDate): boolean {
  if (!row.previous) return false;
  const p = previousPeriod(row.template.frequency, today);
  return row.previous.date >= p.start && row.previous.date <= p.end;
}
